import React, { useState, useEffect, RefObject } from 'react';
import { fetchDeliveredCars, getCar, updateCar } from '../../api/carsApi';

import { getChecklistSessionsByCar, getLatestChecklistSessionByCar } from '../../api/checklistsApi';
import { createPesquisaShare } from '../../api/formsApi';
import { criarShareDoAcompanhamento } from '../../api/acessoAssociadoApi';
import { abrirWhatsApp, montarMensagemDoAcompanhamento } from '../../shared/whatsapp';
import { useIsTi } from '../../hooks/useUserType';
import { useAcompanhamentoAtivo } from '../../hooks/useAcompanhamentoAtivo';
import RecepcaoActionsModal from '../modal/recepcao-actions-modal/RecepcaoActionsModal';
import OwnerContactModal from '../modal/owner-contact/OwnerContactModal';
import EditCarModal from '../modal/edit-car/EditCarModal';
import ChecklistModal from '../modal/checklist/ChecklistModal';
import { TipoServico } from '../modal/start-checklist/StartChecklistModal';
import FullScreenLoader from '../loading/FullScreenLoader';
import ArrowLeftOrange from '../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../img/arrow-right-gray.svg';
import SearchIcon from '../../img/search-icon.svg';
import { notify } from '../../shared/feedback/notify';
import { ehFamiliaEntrada, ehFamiliaSaida } from '../../shared/checklist-familias';
import '../../shared/feedback/erro-de-tela.css';
import '../../shared/ui/badge-status.css';

// Este componente usa as classes de esqueleto compartilhadas (recepcao-header-row,
// recepcao-title) e por isso importa a folha que as define. Depender do CSS que a
// pagina hospedeira carrega e acoplamento invisivel: a aba e usada tanto pela
// Recepcao quanto pelo Chefe de Oficina, e so funcionava porque o CRA junta todo
// o CSS num bundle unico (BUG-V74/at-67).
import '../../views/recepcao/pages/Recepcao.css';

interface EntreguesTabProps {
  sidebarRef?: RefObject<HTMLDivElement>;
  /** Linha de abas (Em atendimento / Entregues) renderizada pela página que hospeda a aba. */
  tabs?: React.ReactNode;
  /** Título da página exibido ao lado da busca (ex: "Recepção", "Chefe de Oficina"). */
  title?: string;
  /** Conteúdo extra renderizado ao lado do título (ex: botão "Como Funciona"), para sobreviver à troca de aba. */
  titleExtra?: React.ReactNode;
}

interface ChecklistFlags {
  hasEntradaCompleted: boolean;
  hasSaidaCompleted: boolean;
  /** Tipo real do checklist da família entrada (ENTRADA ou POS_EVENTO_ENTRADA — at-96). */
  entradaType?: TipoServico;
  /** at-96: tipo real da saída (SAIDA ou POS_EVENTO_SAIDA) — a busca é POR TIPO. */
  saidaType?: TipoServico;
}

const COLUNAS_BASE = 'minmax(120px, 1fr) minmax(150px, 1.2fr) minmax(130px, 1fr) minmax(160px, 1.4fr) minmax(130px, 1fr) minmax(130px, 1fr)';
// at-134: a coluna "Pesquisa" só existe quando há pesquisa de satisfação ativa.
const COLUNA_PESQUISA = 'minmax(170px, 1.2fr)';

const headersBase = [
  'Placa',
  'Modelo',
  'Classe',
  'Proprietário',
  'Data de entrada',
  'Entregue em',
];

const dataCurta = (data?: string | null) => (data ? new Date(data).toLocaleDateString('pt-BR') : '');

/**
 * at-134 — o lembrete da Pesquisa de Satisfação. O envio continua manual (ADR 0031); o que esta
 * coluna faz é tornar VISÍVEL o atendimento entregue que ficou sem pesquisa, que antes passava em
 * silêncio. Quatro estados, e "sem telefone" é próprio: ali o envio é impossível, e cobrá-lo como
 * "não enviada" pediria o que não dá para fazer.
 */
export const estadoDaPesquisa = (car: {
  pesquisa?: { enviadaEm: string; respondidaEm: string | null } | null;
  pesquisaPodeSerEnviada?: boolean;
}): { texto: string; classe: string; titulo: string } => {
  if (car.pesquisa?.respondidaEm) {
    return {
      texto: `Respondida em ${dataCurta(car.pesquisa.respondidaEm)}`,
      classe: 'badge-verde',
      titulo: 'O associado respondeu a pesquisa de satisfação.',
    };
  }
  if (car.pesquisa) {
    return {
      texto: `Enviada em ${dataCurta(car.pesquisa.enviadaEm)}`,
      classe: 'badge-azul',
      titulo: 'O link foi gerado e ainda não foi respondido. Reenviar reaproveita o mesmo link.',
    };
  }
  if (car.pesquisaPodeSerEnviada === false) {
    return {
      texto: 'Sem telefone',
      classe: 'badge-cinza',
      titulo: 'Cadastre o telefone do proprietário em Editar Carro para enviar a pesquisa.',
    };
  }
  return {
    texto: 'Não enviada',
    classe: 'badge-laranja',
    titulo: 'A pesquisa de satisfação ainda não foi enviada. Abra a placa e use Enviar Pesquisa de Satisfação.',
  };
};

/** Aba de atendimentos encerrados (esteira ENTREGA): visualização de checklist, PDF e compartilhamento. */
const EntreguesTab: React.FC<EntreguesTabProps> = ({ sidebarRef, tabs, title = 'Recepção', titleExtra }) => {
  const [cars, setCars] = useState<any[]>([]);
  const [totalCars, setTotalCars] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  // at-134: o lembrete da pesquisa.
  const [pesquisaAtiva, setPesquisaAtiva] = useState(false);
  const [soSemPesquisa, setSoSemPesquisa] = useState(false);
  const [checklistFlagsByCarId, setChecklistFlagsByCarId] = useState<Record<string, ChecklistFlags>>({});
  const [actionsModalOpen, setActionsModalOpen] = useState<boolean>(false);
  const [selectedCarForActions, setSelectedCarForActions] = useState<string | null>(null);
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [checklistCarId, setChecklistCarId] = useState<string | null>(null);
  const [checklistSessionId, setChecklistSessionId] = useState<string | null>(null);
  const [checklistTipoServico, setChecklistTipoServico] = useState<TipoServico>('ENTRADA');
  const [enviandoPesquisa, setEnviandoPesquisa] = useState(false);
  // at-126: a ação "Compartilhar Acompanhamento" está gerando o link.
  const [compartilhandoAcompanhamento, setCompartilhandoAcompanhamento] = useState(false);
  /**
   * O mini modal de contato é o mesmo para as duas ações que exigem telefone (pesquisa e
   * acompanhamento); isto guarda qual delas continua depois de gravar o contato.
   */
  const [acaoAposContato, setAcaoAposContato] = useState<'pesquisa' | 'acompanhamento'>('pesquisa');
  const [carroParaContato, setCarroParaContato] = useState<string | null>(null);
  const [carroParaEditar, setCarroParaEditar] = useState<any | null>(null);
  // Recarga da lista após uma edição: repete a consulta corrente (mesma página/busca).
  const [versaoDaLista, setVersaoDaLista] = useState(0);
  // O TI enxerga esta aba em leitura (ADR 0012) e não tem a rota de envio: sem
  // este gating, o botão apareceria para ele e só devolveria 403.
  const isTi = useIsTi();
  // O botão do acompanhamento só existe com a feature ligada (o TI nunca o recebe).
  const acompanhamentoAtivo = useAcompanhamentoAtivo(!isTi);

  const startCarIndex = totalCars === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endCarIndex = Math.min(currentPage * limit, totalCars);

  const formatDate = (date?: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    });
  };

  const normalizeStatusKey = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');

  useEffect(() => {
    let isMounted = true;

    const loadDelivered = async () => {
      setLoading(true);
      try {
        const response = await fetchDeliveredCars(
          currentPage,
          limit,
          'statusUpdatedAt',
          'desc',
          appliedSearch || undefined,
          soSemPesquisa,
        );
        if (!isMounted) return;
        setCars(response.cars);
        setTotalCars(response.total);
        setTotalPages(Math.max(1, Math.ceil(response.total / limit)));
        setPesquisaAtiva(Boolean(response.pesquisaAtiva));
        setError(null);
      } catch (err) {
        if (!isMounted) return;
        // at-134: a lista é esvaziada e a faixa diz que a consulta falhou. Com os dados antigos na
        // tela, a coluna "Pesquisa" continuaria afirmando estados que ninguém conferiu.
        setCars([]);
        setTotalCars(0);
        setError('Não foi possível carregar os veículos entregues. Recarregue a página; se persistir, acione a TI.');
        notify.errorFrom(err, 'Não foi possível carregar os veículos entregues.', { toastId: 'entregues-carga' });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadDelivered();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, appliedSearch, soSemPesquisa, versaoDaLista]);

  const headers = pesquisaAtiva ? [...headersBase, 'Pesquisa'] : headersBase;
  const tableColumns = pesquisaAtiva ? `${COLUNAS_BASE} ${COLUNA_PESQUISA}` : COLUNAS_BASE;

  useEffect(() => {
    let isMounted = true;

    /**
     * Situação de checklist do atendimento SELECIONADO (at-65, BUG-V36).
     *
     * Era uma consulta por atendimento da página, refeita a cada troca de página e busca —
     * para alimentar botões de um modal que abre um por vez.
     */
    const loadChecklistFlags = async () => {
      if (!selectedCarForActions) return;

      try {
        const sessions = await getChecklistSessionsByCar(selectedCarForActions);
        if (!isMounted) return;

        const normalizedSessions = (sessions || []).map((session: any) => ({
          type: normalizeStatusKey(session?.type),
          status: normalizeStatusKey(session?.status),
        }));

        // at-96: por FAMÍLIA — os tipos de pós-evento contam como entrada e saída.
        const entradaSession = normalizedSessions.find(
          (session: any) => ehFamiliaEntrada(session.type) && session.status === 'COMPLETED'
        );
        const saidaSession = normalizedSessions.find(
          (session: any) => ehFamiliaSaida(session.type) && session.status === 'COMPLETED'
        );

        setChecklistFlagsByCarId((prev) => ({
          ...prev,
          [selectedCarForActions]: {
            hasEntradaCompleted: Boolean(entradaSession),
            hasSaidaCompleted: Boolean(saidaSession),
            entradaType: entradaSession?.type as TipoServico | undefined,
            saidaType: saidaSession?.type as TipoServico | undefined,
          },
        }));
      } catch {
        if (!isMounted) return;
        // Sem sinal, a falha fazia o atendimento parecer "sem checklist".
        setChecklistFlagsByCarId((prev) => ({
          ...prev,
          [selectedCarForActions]: { hasEntradaCompleted: false, hasSaidaCompleted: false },
        }));
        // Texto fixo (não `errorFrom`): num 403 o motivo HTTP substituiria a consequência.
        notify.warning(
          'Não foi possível verificar os checklists deste atendimento. Ele aparece como se não houvesse checklist — recarregue a página; se persistir, acione a TI.',
          { toastId: 'entregues-checklist-flags' },
        );
      }
    };

    void loadChecklistFlags();

    return () => {
      isMounted = false;
    };
  }, [selectedCarForActions]);

  /**
   * Gera o link da pesquisa do atendimento e abre o WhatsApp do atendente com a
   * mensagem configurada — mesmo fluxo do compartilhamento de checklist.
   *
   * O link é montado AQUI, a partir da origem da própria tela: o backend não monta
   * endereço de frontend (a env de origem é uma lista, e interpolá-la produz link
   * quebrado — o defeito do e-mail de redefinição de senha, at-70).
   */
  /**
   * Atendimento entregue não tem caminho de edição (o modal é somente leitura),
   * então a falta de contato do proprietário seria um beco: o envio falha e não há
   * onde cadastrar. Faltando telefone ou nome, o clique abre o mini modal e o envio
   * continua sozinho depois de gravar.
   */
  const contatoIncompleto = (carId: string) => {
    const car = cars.find((item) => item.id === carId);
    return !car?.ownerPhone || !car?.ownerName;
  };

  const handleEnviarPesquisa = async (carId: string) => {
    if (contatoIncompleto(carId)) {
      setAcaoAposContato('pesquisa');
      setCarroParaContato(carId);
      return;
    }
    await gerarLinkEAbrirWhatsApp(carId);
  };

  /**
   * at-126: o acompanhamento segue aberto por 30 dias depois da entrega, então a ação vale
   * também aqui. Mesma regra do contato que a pesquisa: faltando, abre o mini modal e continua.
   */
  const handleCompartilharAcompanhamento = async (carId: string) => {
    if (contatoIncompleto(carId)) {
      setAcaoAposContato('acompanhamento');
      setCarroParaContato(carId);
      return;
    }
    await gerarAcompanhamentoEAbrirWhatsApp(carId);
  };

  const gerarAcompanhamentoEAbrirWhatsApp = async (carId: string) => {
    setCompartilhandoAcompanhamento(true);
    try {
      const share = await criarShareDoAcompanhamento(carId);
      abrirWhatsApp(share.ownerPhone, montarMensagemDoAcompanhamento(share));
      setActionsModalOpen(false);
      setSelectedCarForActions(null);
    } catch (error: any) {
      if (error?.response?.data?.code === 'CAR_WITHOUT_OWNER_PHONE') {
        setAcaoAposContato('acompanhamento');
        setCarroParaContato(carId);
        return;
      }
      // O WhatsApp NÃO abre: abrir sem link faria o atendente acreditar que enviou.
      notify.actionError(error, { action: 'send', subject: 'o link do acompanhamento' });
    } finally {
      setCompartilhandoAcompanhamento(false);
    }
  };

  const gerarLinkEAbrirWhatsApp = async (carId: string) => {
    setEnviandoPesquisa(true);
    try {
      const share = await createPesquisaShare(carId);
      const link = `${window.location.origin}/pesquisa/${share.token}`;
      const mensagem = share.template
        .replace(/\{\{link\}\}/g, link)
        .replace(/\{\{placa\}\}/g, share.plate || '')
        .replace(/\{\{nome\}\}/g, share.ownerName || 'cliente');

      const digitos = String(share.ownerPhone || '').replace(/\D/g, '');
      const comPais = digitos.startsWith('55') && digitos.length >= 12 ? digitos : `55${digitos}`;
      window.open(`https://wa.me/${comPais}?text=${encodeURIComponent(mensagem)}`, '_blank', 'noopener');

      setActionsModalOpen(false);
      setSelectedCarForActions(null);
      // at-134: a coluna "Pesquisa" passa a dizer "Enviada em".
      setVersaoDaLista((v) => v + 1);
    } catch (error: any) {
      // Dado da lista pode estar velho (o telefone saiu por outra tela): se o backend
      // acusar a falta, abre o mesmo mini modal em vez de deixar o usuário sem saída.
      if (error?.response?.data?.code === 'CAR_WITHOUT_OWNER_PHONE') {
        setCarroParaContato(carId);
        return;
      }
      // O motivo vem do backend e é o que orienta quem está na tela: nenhuma pesquisa
      // ativa, ou associado que já respondeu.
      notify.actionError(error, { action: 'send', subject: 'o link da pesquisa' });
    } finally {
      setEnviandoPesquisa(false);
    }
  };

  /**
   * Edição do veículo entregue (decisão do usuário, 18/08/2026): o totem manda o
   * associado procurar a recepção quando os dados não conferem, então a recepção
   * precisa de onde corrigir o cadastro — o PATCH aceita atendimento encerrado.
   */
  const handleEditarEntregue = async (carId: string) => {
    try {
      const detalhes = await getCar(carId);
      setCarroParaEditar(detalhes);
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível carregar o veículo para edição. Recarregue a página; se persistir, acione a TI.');
    }
  };

  const openChecklistReadOnly = async (carId: string, checklistType?: TipoServico) => {
    try {
      let targetSession: any;

      if (checklistType) {
        try {
          targetSession = await getLatestChecklistSessionByCar(carId, {
            type: checklistType,
            status: 'COMPLETED',
          });
        } catch {
          const sessions = await getChecklistSessionsByCar(carId);
          const typedSessions = (sessions || []).filter(
            (session: any) =>
              normalizeStatusKey(session?.type) === checklistType &&
              normalizeStatusKey(session?.status) === 'COMPLETED'
          );
          targetSession = typedSessions[0];
        }
      } else {
        targetSession = await getLatestChecklistSessionByCar(carId);
      }

      if (!targetSession?.id) {
        throw new Error(
          checklistType
            ? `Checklist de ${ehFamiliaSaida(checklistType) ? 'saída' : 'entrada'} ainda não encontrado para este veículo.`
            : 'Checklist ainda não encontrado para este veículo.'
        );
      }

      setChecklistCarId(carId);
      setChecklistSessionId(targetSession.id);
      setChecklistTipoServico((targetSession.type || checklistType || 'ENTRADA') as TipoServico);
      setIsChecklistModalOpen(true);
    } catch (e: any) {
      notify.error(
        e?.message ||
        (checklistType
          ? `Checklist de ${ehFamiliaSaida(checklistType) ? 'saída' : 'entrada'} ainda não encontrado para este veículo.`
          : 'Checklist ainda não encontrado para este veículo.')
      );
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedSearch(searchQuery.trim());
  };

  return (
    <>
      {loading && <FullScreenLoader />}

      {/* at-134: a faixa fica DENTRO do conteúdo, que continua montado — sumir com busca e filtro
          junto com a lista deixava o usuário sem como tentar de novo (BUG-V27). A classe antiga
          (`error`) não existia em CSS nenhum: o aviso saía como texto preto solto. */}
      {!loading && (
        <>
          {error && (
            <div className="erro-de-tela" role="alert">
              {error}
            </div>
          )}
          <div className="recepcao-header-row">
            <div className="recepcao-title">{title}</div>
            {titleExtra}
            <div className="recepcao-search-bar">
              <div className="ItemSearch" onClick={handleSearch}>
                <img
                  className="imgIcon"
                  src={SearchIcon}
                  alt="Ícone de busca"
                  width="20"
                  height="20"
                />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Pesquise por placa, proprietário ou nº do evento"
                className="recepcao-search-input"
              />
            </div>
          </div>
          {tabs}
          <div className="VisualizarOrdenar">
            <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}>
              <div className="CtaBig">
                <div className="Frame1000003987">
                  <div className="ComponentName">Itens por página:</div>
                  <select
                    id="limit-entregues"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="ComponentNameImgIcon"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              {(pesquisaAtiva || soSemPesquisa) && (
                <label
                  className="CtaBig"
                  title="Atendimentos entregues, com telefone, para os quais a pesquisa de satisfação ainda não foi enviada"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="Frame1000003987">
                    <input
                      type="checkbox"
                      checked={soSemPesquisa}
                      onChange={(e) => {
                        setSoSemPesquisa(e.target.checked);
                        setCurrentPage(1);
                      }}
                    />
                    <div className="ComponentName">Só sem pesquisa enviada</div>
                  </div>
                </label>
              )}
            </div>
          </div>
          <div className="QuantidadeDeCarros">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
              }}
            >
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {error
                      ? 'Não foi possível consultar'
                      : `${startCarIndex}-${endCarIndex} de ${totalCars} carros entregues`}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              width: '100%',
              paddingTop: 30,
              background: 'var(--tema-superficie, white)',
              borderRadius: 16,
              border: '1px var(--tema-linha-2, #CACACA) solid',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              marginBottom: 20,
            }}
          >
            <div className="TabelaScroll">
              <div
                className="TabelaContent"
                style={{ ['--table-columns' as any]: tableColumns, width: '100%' }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headers.map((header) => (
                        <div key={header} className="Opcoes">
                          {header}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {cars.length === 0 ? (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                      {/* at-60: "nenhum" só quando a consulta RESPONDEU. */}
                      <div className="Data">
                        {error
                          ? 'A lista não pôde ser carregada.'
                          : soSemPesquisa
                            ? 'Nenhum atendimento entregue aguardando o envio da pesquisa.'
                            : 'Nenhum carro entregue encontrado.'}
                      </div>
                    </div>
                  </div>
                ) : (
                  cars.map((car) => (
                    <div key={car.id} className="Tabela">
                      <div className="Item">
                        <div
                          className="Data"
                          onClick={() => {
                            setSelectedCarForActions(car.id);
                            setActionsModalOpen(true);
                          }}
                          style={{
                            cursor: 'pointer',
                            color: '#ea5d0e',
                            fontWeight: '600',
                            textDecoration: 'underline',
                          }}
                        >
                          {car.plate || '-'}
                        </div>
                      </div>

                      <div className="Item">
                        <div className="Data">{car.model || '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{car.carClass || '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{car.ownerName || '-'}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{formatDate(car.entryDate)}</div>
                      </div>

                      <div className="Item">
                        <div className="Data">{formatDate(car.deliveredAt)}</div>
                      </div>

                      {pesquisaAtiva && (
                        <div className="Item">
                          {(() => {
                            const estado = estadoDaPesquisa(car);
                            return (
                              <div className="Data" title={estado.titulo}>
                                <span className={`cilia-badge ${estado.classe}`} style={{ whiteSpace: 'nowrap' }}>
                                  {estado.texto}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="PaginaO">
              <div
                className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                  }
                }}
                style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <img
                  className="imgIcon"
                  src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                  alt="Seta para Esquerda"
                  width="24"
                  height="24"
                />
                <div
                  className="Anterior"
                  style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Anterior
                </div>
              </div>
              {/*
                Só o NÚMERO dentro do badge: a bolinha (`.Ellipse14`) é um círculo fixo
                de 30px, compartilhado por 4 telas — "1 / 1" estourava o círculo e saía
                quebrado. O total fica ao lado, como texto.
              */}
              <div className="Group21">
                <div className="Group20">
                  <div className="page-number active">
                    {currentPage}
                    <div className="Ellipse14" />
                  </div>
                  <span className="entregues-total-paginas">de {totalPages}</span>
                </div>
              </div>
              <div
                className={`Frame1000003949 ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                  }
                }}
                style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                <div
                  className="PrXima"
                  style={{ color: currentPage === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Próxima
                </div>
                <img
                  className="imgIcon"
                  src={currentPage === totalPages ? ArrowRightGray : ArrowRightOrange}
                  alt="Seta para Direita"
                  width="24"
                  height="24"
                />
              </div>
            </div>
          </div>
        </>
      )}

      <RecepcaoActionsModal
        isOpen={actionsModalOpen}
        carId={selectedCarForActions}
        deliveredMode
        onEnviarPesquisa={isTi ? undefined : handleEnviarPesquisa}
        enviandoPesquisa={enviandoPesquisa}
        situacaoDaPesquisa={(() => {
          // at-134: o estado ao lado do botão, lido da linha da lista (sem chamada nova).
          const car = cars.find((c) => c.id === selectedCarForActions);
          return pesquisaAtiva && car ? estadoDaPesquisa(car).texto : undefined;
        })()}
        onCompartilharAcompanhamento={!isTi && acompanhamentoAtivo ? handleCompartilharAcompanhamento : undefined}
        compartilhandoAcompanhamento={compartilhandoAcompanhamento}
        onEditarEntregue={isTi ? undefined : handleEditarEntregue}
        travarCliqueFora={Boolean(carroParaContato) || Boolean(carroParaEditar)}
        onCancel={() => {
          setActionsModalOpen(false);
          setSelectedCarForActions(null);
        }}
        onEditCar={() => undefined}
        onChangeStatus={() => undefined}
        onViewChecklist={(id, type) => {
          setActionsModalOpen(false);
          // at-96: a sessão é buscada POR TIPO — num pós-evento o tipo real é o de pós-evento,
          // e pedir o genérico faria a tela dizer "não encontrado" sobre o que existe.
          const flags = checklistFlagsByCarId[id];
          const tipoReal = type === 'ENTRADA' ? flags?.entradaType : flags?.saidaType;
          void openChecklistReadOnly(id, tipoReal || type);
        }}
        onStartChecklist={() => undefined}
        onStartChecklistSaida={() => undefined}
        hasEntradaCompleted={
          selectedCarForActions
            ? (checklistFlagsByCarId[selectedCarForActions] || { hasEntradaCompleted: false }).hasEntradaCompleted
            : false
        }
        hasSaidaCompleted={
          selectedCarForActions
            ? (checklistFlagsByCarId[selectedCarForActions] || { hasSaidaCompleted: false }).hasSaidaCompleted
            : false
        }
        sidebarRef={sidebarRef}
      />

      {carroParaContato && (
        <OwnerContactModal
          isOpen
          carId={carroParaContato}
          plate={cars.find((item) => item.id === carroParaContato)?.plate || ''}
          ownerNameAtual={cars.find((item) => item.id === carroParaContato)?.ownerName}
          ownerPhoneAtual={cars.find((item) => item.id === carroParaContato)?.ownerPhone}
          onCancel={() => setCarroParaContato(null)}
          onSaved={async ({ ownerName, ownerPhone }) => {
            const carId = carroParaContato;
            // A lista é atualizada localmente para o modal de ações refletir o
            // contato novo sem uma recarga inteira da aba.
            setCars((atual) =>
              atual.map((item) => (item.id === carId ? { ...item, ownerName, ownerPhone } : item))
            );
            setCarroParaContato(null);
            if (acaoAposContato === 'acompanhamento') await gerarAcompanhamentoEAbrirWhatsApp(carId);
            else await gerarLinkEAbrirWhatsApp(carId);
          }}
        />
      )}

      <EditCarModal
        isOpen={Boolean(carroParaEditar)}
        onCancel={() => setCarroParaEditar(null)}
        carDetails={carroParaEditar}
        onUpdate={async (id: string, data: any) => {
          await updateCar(id, data);
          setVersaoDaLista((v) => v + 1);
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={false}
      />

      <ChecklistModal
        isOpen={isChecklistModalOpen}
        onClose={() => {
          setIsChecklistModalOpen(false);
          setChecklistCarId(null);
          setChecklistSessionId(null);
        }}
        carId={checklistCarId || ''}
        sessionId={checklistSessionId}
        tipoServico={checklistTipoServico}
        readOnly
        // at-65 (BUG-V11): aba "Entregues" é consulta de atendimento encerrado.
        permitirEdicao={false}
      />
    </>
  );
};

export default EntreguesTab;
